import { motion, AnimatePresence } from "motion/react";
import { Layers, Lightbulb, Search, ShieldCheck, Sparkles } from "lucide-react";

/**
 * "O'ylayapti" holati.
 *
 * === Qadamlar O'YLAB TOPILMAYDI ===
 *
 * Maketda qadamlar taymer bilan almashardi ("2-qadam 450ms dan keyin").
 * Bu yolg'on: ekranda ko'ringan qadam serverdagi haqiqiy ish bilan
 * bog'liq emas edi.
 *
 * Endi backend `trace` hodisalarini yuboradi va shu yerda AYNAN ular
 * ko'rsatiladi.
 */

// Qadam belgisi — matnga qarab tanlanadi. Faqat bezak, mantiq emas.
const ICON_FOR = [
  [/qidir|tahlil/i, Search],
  [/kod|daraja|tanlan/i, Layers],
  [/tekshir|qoida|manba/i, ShieldCheck],
];

const iconFor = (label) =>
  ICON_FOR.find(([re]) => re.test(label))?.[1] ?? Sparkles;

const FACTS = [
  "TIF TN kodi 10 raqamdan iborat: 4 xona guruh, 6 xona kichik guruh, 10 xona aniq tovar.",
  "Tovar ikkita pozitsiyaga to'g'ri kelsa, aniqroq ta'rif beradigani tanlanadi (OPI 3a).",
  "Tasniflash avvalo pozitsiya matni va bo'lim, guruh izohlariga asoslanadi (OPI 1).",
  "Bir xil tovar turli mamlakatlarda birinchi 6 xonasi bilan bir xil kodlanadi.",
  "To'plam holidagi tovar uni asosiy xususiyatini beruvchi qism bo'yicha tasniflanadi.",
];

export function Thinking({ trace }) {
  const steps = trace.length ? trace : ["so'rov tahlil qilinmoqda"];
  const active = steps[steps.length - 1];
  const StepIcon = iconFor(active);

  // Kutish davomida bir xil fakt tursin — har kadrda almashsa
  // o'qib bo'lmaydi. Savol matniga bog'lash mumkin emas (u bu yerda
  // yo'q), shuning uchun qadam soniga bog'lanadi.
  const fact = FACTS[steps.length % FACTS.length];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="my-2 flex w-full flex-col gap-2.5"
    >
      <div className="relative overflow-hidden rounded-2xl border border-sky-500/30 bg-gradient-to-r from-[#061c3b]/95 via-[#08274f]/90 to-[#061c3b]/95 p-4 shadow-lg shadow-sky-950/40 sm:p-5">
        {/* Yugurib o'tuvchi yorug'lik — kutish jonli ko'rinsin. */}
        <motion.div
          animate={{ x: ["-100%", "200%"] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          className="pointer-events-none absolute inset-y-0 w-32 bg-gradient-to-r from-transparent via-sky-400/10 to-transparent"
        />

        <div className="relative z-10 flex items-center gap-4">
          <div className="flex items-center gap-3.5">
            {/* Tarqaluvchi halqalar + aylanuvchi belgi */}
            <div className="relative flex items-center justify-center">
              <motion.span
                animate={{ scale: [1, 1.7, 2], opacity: [0.6, 0.2, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                className="absolute h-8 w-8 rounded-full bg-sky-400/30"
              />
              <motion.span
                animate={{ scale: [1, 1.4, 1.8], opacity: [0.5, 0.15, 0] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  delay: 0.4,
                  ease: "easeOut",
                }}
                className="absolute h-8 w-8 rounded-full bg-cyan-400/20"
              />

              <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 via-sky-600 to-blue-700 p-0.5 shadow-md shadow-sky-500/30">
                <div className="flex h-full w-full items-center justify-center rounded-[10px] bg-[#051733]">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkles className="h-4 w-4 text-sky-300" />
                  </motion.div>
                </div>
              </div>
            </div>

            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-wide text-white sm:text-sm">
                  AIExpert tahlil qilmoqda
                </span>

                {/* Tovush to'lqini — beshta ustun */}
                <div className="flex h-3 items-center gap-0.5">
                  {[0.4, 0.8, 0.5, 1, 0.6].map((from, i) => (
                    <motion.span
                      key={i}
                      animate={{ scaleY: [from, 1.4, 0.3, from] }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        delay: i * 0.12,
                        ease: "easeInOut",
                      }}
                      className="h-full w-1 origin-bottom rounded-full bg-gradient-to-t from-sky-500 to-cyan-300"
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-1.5 text-[11px] text-sky-300/70">
                <span className="h-1.5 w-1.5 animate-ping rounded-full bg-emerald-400" />
                <span>Rasmiy TIF TN ma'lumotlar bazasi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Joriy qadam — backend aytgan matn. */}
        <div className="mt-3.5 border-t border-sky-800/40 pt-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-2 text-xs font-medium text-sky-100 sm:text-sm"
            >
              <div className="shrink-0 rounded-md border border-sky-700/50 bg-sky-900/60 p-1 text-sky-300">
                <StepIcon className="h-3.5 w-3.5 animate-pulse" />
              </div>
              <span className="truncate">{active}</span>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="flex select-none items-start gap-2 rounded-xl border border-sky-800/30 bg-[#041228]/70 px-3.5 py-2 text-[11px] leading-snug text-sky-300/75">
        <Lightbulb className="mt-px h-3.5 w-3.5 shrink-0 text-amber-400" />
        <span className="min-w-0">
          <span className="font-semibold text-sky-200">Foydali fakt: </span>
          <span className="text-slate-300">{fact}</span>
        </span>
      </div>
    </motion.div>
  );
}
